import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type PomodoroMode = 'work' | 'break';

export const POMODORO_SECONDS: Record<PomodoroMode, number> = {
  work: 25 * 60,
  break: 5 * 60,
};

export function formatPomodoroTime(seconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  return String(Math.floor(safeSeconds / 60)).padStart(2, '0') + ':' + String(safeSeconds % 60).padStart(2, '0');
}

export function pomodoroAlertText(mode: PomodoroMode): string {
  return mode === 'work' ? 'Time to work' : 'Time to break';
}

export function advancePomodoro(mode: PomodoroMode, deadline: number, now: number): { mode: PomodoroMode; deadline: number; remaining: number } {
  let currentMode = mode;
  let currentDeadline = deadline;
  while (now >= currentDeadline) {
    currentMode = currentMode === 'work' ? 'break' : 'work';
    currentDeadline += POMODORO_SECONDS[currentMode] * 1000;
  }
  return {
    mode: currentMode,
    deadline: currentDeadline,
    remaining: Math.max(0, Math.ceil((currentDeadline - now) / 1000)),
  };
}

export default function PomodoroTimer() {
  const [mode, setMode] = useState<PomodoroMode>('work');
  const [remaining, setRemaining] = useState(POMODORO_SECONDS.work);
  const [running, setRunning] = useState(false);
  const [alertMode, setAlertMode] = useState<PomodoroMode | null>(null);
  const modeRef = useRef<PomodoroMode>('work');
  const deadlineRef = useRef<number | null>(null);
  const audioRef = useRef<AudioContext | null>(null);

  const ensureAudio = useCallback((): AudioContext | null => {
    try {
      const audioWindow = window as typeof window & { webkitAudioContext?: typeof AudioContext };
      const AudioContextConstructor = window.AudioContext || audioWindow.webkitAudioContext;
      if (!AudioContextConstructor) return null;
      if (!audioRef.current || audioRef.current.state === 'closed') audioRef.current = new AudioContextConstructor();
      if (audioRef.current.state === 'suspended') void audioRef.current.resume().catch(() => undefined);
      return audioRef.current;
    } catch {
      return null;
    }
  }, []);

  const playChime = useCallback(async (nextMode: PomodoroMode) => {
    const context = ensureAudio();
    if (!context) return;
    try {
      if (context.state === 'suspended') await context.resume();
      const notes = nextMode === 'work' ? [523.25, 659.25, 783.99] : [659.25, 523.25, 392];
      const start = context.currentTime + 0.04;
      notes.forEach((frequency, index) => {
        const noteStart = start + index * 0.18;
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = nextMode === 'work' ? 'triangle' : 'sine';
        oscillator.frequency.setValueAtTime(frequency, noteStart);
        gain.gain.setValueAtTime(0.0001, noteStart);
        gain.gain.exponentialRampToValueAtTime(nextMode === 'work' ? 0.12 : 0.09, noteStart + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.0001, noteStart + 0.32);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(noteStart);
        oscillator.stop(noteStart + 0.34);
      });
    } catch {
      // The visual alert still works when a browser blocks audio output.
    }
  }, [ensureAudio]);

  const announceSession = useCallback((nextMode: PomodoroMode) => {
    setAlertMode(nextMode);
    void playChime(nextMode);
  }, [playChime]);

  useEffect(() => {
    if (!running) return undefined;
    const tick = () => {
      if (deadlineRef.current === null) return;
      const next = advancePomodoro(modeRef.current, deadlineRef.current, Date.now());
      deadlineRef.current = next.deadline;
      if (next.mode !== modeRef.current) {
        modeRef.current = next.mode;
        setMode(next.mode);
        announceSession(next.mode);
      }
      setRemaining(next.remaining);
    };
    tick();
    const interval = window.setInterval(tick, 250);
    return () => window.clearInterval(interval);
  }, [announceSession, running]);

  useEffect(() => {
    if (!alertMode) return undefined;
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAlertMode(null);
    };
    window.addEventListener('keydown', dismissOnEscape);
    return () => window.removeEventListener('keydown', dismissOnEscape);
  }, [alertMode]);

  useEffect(() => () => {
    if (audioRef.current && audioRef.current.state !== 'closed') void audioRef.current.close().catch(() => undefined);
  }, []);

  const toggle = () => {
    if (running) {
      if (deadlineRef.current !== null) {
        const paused = advancePomodoro(modeRef.current, deadlineRef.current, Date.now());
        modeRef.current = paused.mode;
        setMode(paused.mode);
        setRemaining(paused.remaining);
      }
      deadlineRef.current = null;
      setRunning(false);
      return;
    }
    ensureAudio();
    deadlineRef.current = Date.now() + remaining * 1000;
    setRunning(true);
  };

  const reset = () => {
    deadlineRef.current = null;
    setRunning(false);
    setRemaining(POMODORO_SECONDS[modeRef.current]);
  };

  const nextSession = () => {
    const nextMode: PomodoroMode = modeRef.current === 'work' ? 'break' : 'work';
    modeRef.current = nextMode;
    setMode(nextMode);
    setRemaining(POMODORO_SECONDS[nextMode]);
    deadlineRef.current = running ? Date.now() + POMODORO_SECONDS[nextMode] * 1000 : null;
    announceSession(nextMode);
  };

  const modeLabel = mode === 'work' ? 'Work' : 'Break';
  const timeLabel = formatPomodoroTime(remaining);

  return <>
    <div className={'pomodoro-timer pomodoro-' + mode} aria-label={modeLabel + ' timer, ' + timeLabel}>
      <span className="pomodoro-phase"><i aria-hidden="true" />{modeLabel}</span>
      <time className="pomodoro-time" dateTime={'PT' + remaining + 'S'}>{timeLabel}</time>
      <button type="button" className="pomodoro-control" onClick={toggle} aria-label={(running ? 'Pause ' : 'Start ') + modeLabel.toLowerCase() + ' timer'} title={running ? 'Pause timer' : 'Start timer'}>{running ? 'Ⅱ' : '▶'}</button>
      <button type="button" className="pomodoro-control" onClick={reset} aria-label={'Reset ' + modeLabel.toLowerCase() + ' timer'} title="Reset timer">↺</button>
      <button type="button" className="pomodoro-control" onClick={nextSession} aria-label={'Next session: ' + (mode === 'work' ? 'break' : 'work')} title="Next session">→</button>
    </div>
    {alertMode && createPortal(<div className={'pomodoro-alert pomodoro-alert-' + alertMode} role="alertdialog" aria-modal="true" aria-labelledby="pomodoro-alert-heading">
      <div className="pomodoro-alert-card">
        <span className="pomodoro-alert-icon" aria-hidden="true">{alertMode === 'work' ? '▶' : 'Ⅱ'}</span>
        <p>{alertMode === 'work' ? '25 minute focus session' : '5 minute reset'}</p>
        <h2 id="pomodoro-alert-heading">{pomodoroAlertText(alertMode)}</h2>
        <span>{alertMode === 'work' ? 'Your focus session starts now.' : 'Step away, breathe, and recharge.'}</span>
        <button type="button" autoFocus onClick={() => setAlertMode(null)}>Got it</button>
      </div>
    </div>, document.body)}
  </>;
}
